import { useMemo } from 'react';
import { useI18n } from '../i18n';
import { Prose } from '../chat/Prose';
import { SAMPLES, fill, sampleSlots } from './samples';

/**
 * The worked examples, rendered in the chat and in nothing else. F-141, step 3.
 *
 * *"you will get briefings — they look like this, and they are knowledge. Then questions — they
 * look like this."* The learner has never seen either shape and the product's whole value arrives
 * in them; this is the difference between a first briefing that reads as an odd wall of text and
 * one that reads as the thing they were promised.
 *
 * ---
 *
 * **Everything about this component is the constraint that they must not be mistakable for
 * messages**, and it is stated four separate ways because one of them going stale must not silently
 * undo it:
 *
 * 1. **They are never written to `MSG#`.** Nothing here posts. A sample in the message log is in
 *    the learner's transcript for ever *and* in the operator's timeline the day somebody reports a
 *    problem, with nothing to distinguish it from a briefing the sweep actually sent.
 * 2. **They are not answerable.** There is no item behind the sample question, so a reply to it
 *    would be graded against nothing and enter no review state — *"a worse first experience than no
 *    sample at all"*. The note under them says so in words.
 * 3. **They are not entries in the thread's live log.** They are not `.chat-slot` children, so they
 *    are outside the message column visually — and, because the block renders *inside*
 *    `div.chat-thread[role="log" aria-live="polite"]` and cannot be moved out of it without losing
 *    the scroller, `aria-live="off"` is set here to stop the inherited politeness announcing two
 *    whole sample bodies as something that has just arrived. `role="group"` rather than the
 *    implicit `complementary`, because a landmark nested inside a log is not a structure assistive
 *    technology handles predictably. (The first version of this claimed the aside was outside the
 *    log. It never was; the class was.)
 * 4. **Every bubble carries the word.** `tour.tag` renders in place of the timestamp a real
 *    message carries, in both languages, in text rather than in colour.
 *
 * What is deliberately *not* different is the bubble itself: the same `chat-msg-*` classes and the
 * same `Prose` renderer as a real message. A sample that did not resemble the thing it illustrates
 * would teach the wrong shape, which is the only way this can fail at its actual job.
 *
 * ---
 *
 * **Hybrid content**, which is the owner's answer: *"fixed templates with a few variables that
 * makes them feel real to their objective."* The shapes and the words are fixed, translated and
 * always correct; the learner's own subject titles fill the slots. See `samples.ts`, where the
 * filling is asserted from plain Node.
 */
export default function Samples({
  /** The learner's researched subject titles, best first. May be empty — the outline takes a minute. */
  subjects,
  /** Their objective's title, as the fallback when no subject has landed yet. */
  objective,
  /** Ends the tour. Writes the mark; nothing is sent anywhere. */
  onDone,
}: {
  subjects: readonly (string | undefined)[];
  objective?: string | undefined;
  onDone: () => void;
}) {
  const { t } = useI18n();

  const slots = useMemo(
    () => sampleSlots(subjects, objective, t('tour.subject.fallback')),
    [subjects, objective, t],
  );

  return (
    <aside
      className="chat-samples"
      role="group"
      aria-label={t('tour.title')}
      /* See the note above: this element is a descendant of the thread's `aria-live="polite"`
         log, and a descendant may override the inherited value. Without it, the block appearing
         while the chat is already mounted is read out in full as a new arrival. */
      aria-live="off"
    >
      <h3 className="chat-samples-title">{t('tour.title')}</h3>
      <p className="chat-samples-lede">{t('tour.lede')}</p>

      {SAMPLES.map((sample) => (
        <article
          key={sample.body}
          className={`chat-msg chat-msg-${sample.kind}`}
          /*
           * Not `aria-hidden`, which was the obvious way to keep these out of the way and is
           * exactly wrong: the examples are the content of this block, and hiding them would leave
           * a screen reader with a heading, a note about illustrations, and nothing illustrated.
           */
        >
          <p className="chat-msg-kind">
            {t(sample.kind === 'briefing' ? 'chat.kind.briefing' : 'chat.kind.question')}
          </p>
          <div className="chat-msg-body">
            {/* The same renderer as a real message, so the paragraphs break the same way. It
                escapes everything — the learner's own subject title arrives here as characters. */}
            <Prose text={fill(t(sample.body), slots)} />
          </div>
          {/* Where a real message puts its time. A sample has no time, because it did not
              happen, and the word that goes there instead is the one thing this block cannot
              afford to leave to styling. */}
          <p className="chat-msg-foot">
            <span className="chat-sample-tag">{t('tour.tag')}</span>
          </p>
        </article>
      ))}

      <p className="chat-samples-note">{t('tour.note')}</p>
      <p className="chat-samples-done">
        <button type="button" className="chat-linky" onClick={onDone}>
          {t('tour.done')}
        </button>
      </p>
    </aside>
  );
}
